if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.ctHardenSubmit();
      }

      // --- CorkTree buy-box regression guard ------------------------------
      // ROOT CAUSE this neutralises: the wine-club buy-box section (product card)
      // gained a validation gate whose document-level click listener runs
      // `addBtn.disabled = !valid()` in the bubble phase of the very click meant
      // to submit. A submit button that turns disabled mid-click has its form
      // submission cancelled by the browser — so the first Add to Cart click did
      // nothing and the button "barely worked". This lives in the JS that owns
      // the submit so it can't be defeated by a sibling script, no matter what
      // the section markup does.
      ctHardenSubmit() {
        const btn = this.submitButton;
        if (!btn || btn.dataset.ctHardened) return;
        btn.dataset.ctHardened = '1';

        // Refuse `disabled = true` only while a press on THIS button is resolving.
        const desc = Object.getOwnPropertyDescriptor(HTMLButtonElement.prototype, 'disabled');
        if (desc && desc.set) {
          let pressing = false;
          const release = () => { pressing = false; };
          const press = () => { pressing = true; setTimeout(release, 1000); };
          ['pointerdown', 'mousedown', 'touchstart'].forEach((t) =>
            btn.addEventListener(t, press, { capture: true, passive: true })
          );
          btn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') press(); }, true);
          // Submission runs after click dispatch, same task; a 0ms timer clears the
          // window only once submission is safely past.
          btn.addEventListener('click', () => setTimeout(release, 0), false);
          Object.defineProperty(btn, 'disabled', {
            configurable: true,
            get: () => desc.get.call(btn),
            set: (v) => { if (v && pressing) return; desc.set.call(btn, v); },
          });
        }

        // Make the press visible. product-form.js adds .loading to the BUTTON and
        // unhides .loading__spinner, but the section stylesheet targeted
        // `.loading-spinner` on `.ct-pdp__product-form` — neither selector ever
        // matched and the SVG had no stroke colour, so the spinner was invisible
        // and the button looked dead for the whole request.
        if (!document.getElementById('ct-buybox-feedback-css')) {
          const style = document.createElement('style');
          style.id = 'ct-buybox-feedback-css';
          style.textContent = [
            '.ct-pdp__add-btn{position:relative;touch-action:manipulation;-webkit-tap-highlight-color:transparent;transition:background-color .15s ease,border-color .15s ease,transform .06s ease}',
            '.ct-pdp__add-btn:active:not([disabled]){transform:scale(.985)}',
            '.ct-pdp__add-btn .loading__spinner{position:absolute;inset:0;display:none;align-items:center;justify-content:center;width:auto;height:100%}',
            '.ct-pdp__add-btn .loading__spinner .spinner{width:1.8rem;height:1.8rem}',
            '.ct-pdp__add-btn .loading__spinner .path{stroke:var(--ct-parchment,#f4efe6)}',
            '.ct-pdp__add-btn.loading{cursor:progress}',
            '.ct-pdp__add-btn.loading>span{visibility:hidden}',
            '.ct-pdp__add-btn.loading .loading__spinner{display:flex}',
          ].join('');
          document.head.appendChild(style);
        }
      }

      // POST /cart/add, tolerating Shopify's intermittent bot-protection challenge.
      // The challenge comes back as an HTML "Verifying your connection..." page (usually
      // HTTP 429, cf-mitigated: challenge) instead of JSON; response.json() then throws and
      // the add silently does nothing — the "works, then doesn't" the customer sees.
      // We retry a couple of times (the clearance cookie is normally set within a second),
      // and if it's still HTML we throw so the caller can hand off to a native POST.
      // A genuine Shopify cart error (sold out, etc.) is valid JSON and passes straight through.
      ctPostCartAdd(config, attempt = 0) {
        const MAX_RETRIES = 2;
        return fetch(`${routes.cart_add_url}`, config).then((response) => {
          const type = response.headers.get('content-type') || '';
          if (type.includes('json') || type.includes('javascript')) return response.json();
          if (attempt < MAX_RETRIES) {
            const wait = 400 * (attempt + 1);
            return new Promise((r) => setTimeout(r, wait)).then(() => this.ctPostCartAdd(config, attempt + 1));
          }
          throw new Error(`cart/add returned ${response.status} ${type || 'unknown type'} (bot challenge)`);
        });
      }

      // Last-resort but ALWAYS-WORKS path: a native form POST. The browser itself renders and
      // clears any Shopify bot challenge, then the item is added and the customer lands on /cart.
      // A dead button that does nothing is the worst outcome; this guarantees the add completes.
      // form.submit() fires no submit event, so it can't loop back into this handler.
      ctFallbackSubmit() {
        try {
          this.ctInFlight = false;
          if (!this.form.querySelector('input[name="return_to"]')) {
            const rt = document.createElement('input');
            rt.type = 'hidden';
            rt.name = 'return_to';
            rt.value = '/cart';
            this.form.appendChild(rt);
          }
          this.form.submit();
        } catch (err) {
          console.error(err);
          this.handleErrorMessage(
            (window.variantStrings && window.variantStrings.addToCartError) ||
              "We couldn't add that to your cart just now. Please try again."
          );
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Second guard against a double-add: a plain in-flight flag that no outside
        // code can clear (the section's per-click sync can strip aria-disabled).
        if (this.ctInFlight) return;
        this.ctInFlight = true;
        // Did the server actually accept the add? Guards the catch below from firing a
        // native re-POST (double-add) when the failure was only in rendering the drawer.
        this.ctAdded = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.ctPostCartAdd(config)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Past this point the server has accepted the item — record it so a later
            // rendering error can't trigger a native re-POST that adds it twice.
            this.ctAdded = true;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            // If the add already landed, the failure was only in rendering the drawer —
            // clear the transient error and stop; do NOT re-POST (that would double-add).
            if (this.ctAdded) {
              this.handleErrorMessage();
              return;
            }
            // Otherwise the POST itself failed: a bot challenge returning HTML, a network
            // blip, or an unparseable body. Never dead-end on an error the customer can't
            // act on — fall through to a native form POST so the item still gets added.
            this.ctFallbackSubmit();
          })
          .finally(() => {
            this.ctInFlight = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
